<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed } from 'vue'

import { BoxComponent, FlexComponent, TooltipComponent, TypographyComponent } from '../../..'
import HelpIcon from '../../../../assets/icons/help.svg?component'
import { formatLongValue } from '../../../../formatters'
import type { AffiliateLinkMetrics, ContentItemLinkDetails } from '../../../../types'
import { deriveAffiliateLinkKpis } from '../../../../utils'
import { useDetailFormatters } from '../composables'
import { DETAILS_TOOLTIP_Z_INDEX, type MetricRow, type ViewMode } from '../content-item-details.types'

import ContentItemDetailRow from './ContentItemDetailRow.vue'
const props = defineProps<{
  data: ContentItemLinkDetails
  viewMode: ViewMode
  hideCosts?: boolean
  hideOrders?: boolean
  affiliateLinkMetrics?: AffiliateLinkMetrics
}>()
const { formatInteger, formatMoney } = useDetailFormatters()
const orderValue = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.orderValue : props.data.affiliateLinkStats?.orderValue,
)
const numberOfOrders = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.numberOfOrders : props.data.affiliateLinkStats?.numberOfOrders,
)
const estimatedCostsRow = computed(() => {
  const metrics = props.affiliateLinkMetrics
  if (props.viewMode === 'admin') {
    return {
      label: 'Est. creator costs',
      value: metrics ? metrics.costCreatorEstimated : props.data.affiliateLinkStats?.estimatedCreatorCosts,
    }
  }
  if (props.hideCosts) {
    return null
  }
  return {
    label: 'Est. costs',
    value: metrics ? metrics.revenueInvoiceEstimated : props.data.affiliateLinkStats?.estimatedAdvertiserCosts,
  }
})
const linkFinanceMetricRows = computed<MetricRow[]>(() => {
  if (!props.affiliateLinkMetrics) {
    return []
  }
  const { roas, ers, avgCostPerClick } = deriveAffiliateLinkKpis(props.affiliateLinkMetrics, props.viewMode)
  const costLabel = props.viewMode === 'admin' ? 'estimated creator cost' : 'estimated cost'
  const costLabelCapitalized = props.viewMode === 'admin' ? 'Estimated creator cost' : 'Estimated cost'
  const rows: MetricRow[] = []
  if (!props.hideOrders && estimatedCostsRow.value) {
    rows.push({
      label: props.viewMode === 'admin' ? 'Metapic ROAS' : 'ROAS',
      value: formatLongValue(roas, 'number'),
      tooltip: { title: 'Return on ad spend', description: `Order value divided by ${costLabel}.` },
    })
    rows.push({
      label: props.viewMode === 'admin' ? 'Metapic ERS' : 'ERS',
      value: formatLongValue(ers, 'percentage'),
      tooltip: { title: 'Effective revenue share', description: `${costLabelCapitalized} divided by order value.` },
    })
  }
  if (estimatedCostsRow.value) {
    rows.push({
      label: 'Avg. cost per click',
      value: formatMoney(avgCostPerClick),
      tooltip: {
        title: 'Average cost per click',
        description: `${costLabelCapitalized} divided by the number of clicks.`,
      },
    })
  }
  return rows
})
const show = computed(() => !props.hideOrders || Boolean(estimatedCostsRow.value) || linkFinanceMetricRows.value.length > 0)
</script>
<template>
  <BoxComponent v-if="show">
    <BoxComponent :mb="24">
      <TypographyComponent variant="headline-small">Orders & costs</TypographyComponent>
    </BoxComponent>
    <FlexComponent :gap="16" direction="column">
      <ContentItemDetailRow v-if="!hideOrders" label="Order value">
        <TypographyComponent variant="body-medium">{{ formatMoney(orderValue) }}</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow v-if="estimatedCostsRow" :label="estimatedCostsRow.label">
        <TypographyComponent variant="body-medium">{{ formatMoney(estimatedCostsRow.value) }}</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow v-if="!hideOrders" label="Orders">
        <TypographyComponent variant="body-medium">{{ formatInteger(numberOfOrders) }}</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow v-for="metric in linkFinanceMetricRows" :key="metric.label">
        <template #label>
          <FlexComponent :gap="4" align="center">
            <TypographyComponent variant="title-medium" :font-weight="600">{{ metric.label }}</TypographyComponent>
            <TooltipComponent v-if="metric.tooltip" placement="top" :z-index="DETAILS_TOOLTIP_Z_INDEX">
              <button type="button" class="content-item-details__metric-help" :aria-label="metric.tooltip.description">
                <ElIcon :size="16">
                  <HelpIcon />
                </ElIcon>
              </button>
              <template #content>
                <FlexComponent direction="column" :gap="4">
                  <TypographyComponent v-if="metric.tooltip.title" variant="title-medium">
                    {{ metric.tooltip.title }}
                  </TypographyComponent>
                  <TypographyComponent variant="body-medium">{{ metric.tooltip.description }}</TypographyComponent>
                </FlexComponent>
              </template>
            </TooltipComponent>
          </FlexComponent>
        </template>
        <TypographyComponent variant="body-medium">{{ metric.value }}</TypographyComponent>
      </ContentItemDetailRow>
    </FlexComponent>
  </BoxComponent>
</template>
<style scoped lang="scss">
.content-item-details__metric-help {
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--secondary-48);
  cursor: help;
}
</style>
